import { useEffect, useState } from "react";
import { ethers } from "ethers";
import { parsePrivateAddress } from "../../utils/inbox-crypto";
import {
  isStealthHandle,
  resolveStealthHandle,
} from "../../utils/handle-resolver";

export function useSendRecipient(recipient: string, enabled: boolean) {
  const value = recipient.trim();
  const handle =
    !ethers.isAddress(value) &&
    !parsePrivateAddress(value) &&
    isStealthHandle(value);
  const [checked, setChecked] = useState<{
    value: string;
    error: string | null;
  } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setChecked(null);
    if (!enabled || !handle) return;
    const stop = new AbortController();
    const timer = setTimeout(() => {
      void resolveStealthHandle(value, "base", { signal: stop.signal })
        .then((record) => {
          if (!record.mpkHex || !record.inboxPublicKeyHex)
            throw Error("This handle cannot receive private payments yet.");
          if (!stop.signal.aborted) setChecked({ value, error: null });
        })
        .catch((error) => {
          if (!stop.signal.aborted)
            setChecked({
              value,
              error:
                error instanceof Error
                  ? error.message
                  : "Could not verify this handle. Please try again.",
            });
        });
    }, 350);
    return () => {
      clearTimeout(timer);
      stop.abort();
    };
  }, [value, enabled, handle, attempt]);
  const privateAddress = parsePrivateAddress(value);
  const direct =
    ethers.isAddress(value) ||
    !!(privateAddress?.mpkHex && privateAddress.inboxPublicKeyHex);
  const current = checked?.value === value ? checked : null;
  const error = direct
    ? null
    : handle
      ? current?.error || null
      : "Enter a valid wallet address, private address or .stealth handle.";
  return {
    valid: enabled && (direct || !!(handle && current && !current.error)),
    message:
      error ||
      (handle ? (current ? "Handle verified" : "Checking recipient…") : ""),
    error,
    retry: () => {
      setChecked(null);
      setAttempt((n) => n + 1);
    },
  };
}
